import { Button } from '@mui/material';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Localized, PoolFormattedMessage } from '@synergy/components';
import { PaloColumn, PaloConfirmationDialog, PaloListView, PaloRowAction } from '@synergy/components-palo';
import { ReactElement, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import * as StaticSitesActions from '../store/static-sites-actions';
import * as StaticSitesListActions from '../static-sites-list/store/static-sites-list-actions';
import * as StaticSitesListSelectors from '../static-sites-list/store/static-sites-list-selectors';
import { ROUTES } from '../../../sidebar-menu/navigation-drawer/routes';
import { StaticSitesDto } from '../models/static-sites-dto';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { PaloFilterPanel } from '../../../../common/components/palo/palo-filter-panel';
import { usePaloColumnFilters } from '../../../../common/components/palo/use-palo-column-filters';
import { usePaloListViews } from '../../../../common/components/palo/use-palo-list-views';
import { usePaloSort } from '../../../../common/components/palo/use-palo-sort';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const columns: PaloColumn<StaticSitesDto>[] = [
    {
        key: 'title',
        type: 'name',
        sortKey: 'title',
        head: <FormattedMessage id="app.table.column.title" />,
        cell: site => <Localized value={site.title} />,
    },
    {
        key: 'path',
        type: 'name',
        head: <FormattedMessage id="app.table.column.path" />,
        cell: site => site.path,
    },
    {
        key: 'updatedAt',
        type: 'date',
        sortKey: 'updatedAt',
        head: <FormattedMessage id="app.table.column.updatedAt" />,
        cell: site => <FormattedDate value={site.updatedAt} year="numeric" month="short" day="2-digit" />,
    },
    {
        key: 'createdAt',
        type: 'date',
        head: <FormattedMessage id="app.table.column.createdAt" />,
        cell: site => <FormattedDate value={site.createdAt} year="numeric" month="short" day="2-digit" />,
    },
];

export const PaloStaticSitesList = (): ReactElement => {
    const { hiddenColumnKeys, shownColumnKeys, toggleColumn, moveColumn, orderColumns, savedFiltersMenu } =
        usePaloListViews('static-sites');
    const columnFilter = usePaloColumnFilters('static-sites');
    const intl = useIntl();
    const dispatch = useThunkDispatch();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { sort, params } = usePaloSort('updatedAt', 'desc');
    const requesting = useAppSelector(StaticSitesListSelectors.staticSitesListRequestingSelector);
    const paginated = useAppSelector(StaticSitesListSelectors.staticSitesListItemsSelector);
    const currentLocale = useAppSelector(state => state.currentLocale);
    const [toDelete, setToDelete] = useState<number | null>(null);

    const page = Number(searchParams.get('page') ?? 1); // 1-based
    const limit = Number(searchParams.get('limit') ?? 50);

    useEffect(() => {
        dispatch(StaticSitesListActions.fetchStaticSitesList(params));
    }, [dispatch, params]);

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const openDetail = (site: StaticSitesDto): void =>
        navigate(ROUTES.contentManagement.staticSites.detail.replace(':id', site.id.toString()));

    const handleDelete = (): void => {
        if (toDelete) {
            dispatch(StaticSitesActions.deleteStaticSites(toDelete));
            setToDelete(null);
        }
    };

    const rowActions = (site: StaticSitesDto): PaloRowAction[] => [
        { label: 'Öffnen', onClick: () => openDetail(site) },
        { label: 'Löschen', onClick: () => setToDelete(site.id) },
    ];

    const headerActions = (
        <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(`${ROUTES.contentManagement.staticSites.list}/new`)}
        >
            <FormattedMessage id="app.contentManagement.newStaticSites" />
        </Button>
    );

    return (
        <PaloListView<StaticSitesDto>
            pageTitle={intl.formatMessage({ id: 'app.contentManagement.staticSites' })}
            sort={sort}
            onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
            title={<FormattedMessage id="app.contentManagement.staticSites" />}
            headerActions={headerActions}
            filters={
                <>
                    <PaloFilterPanel listKey="static-sites" />
                    {savedFiltersMenu}
                </>
            }
            columns={orderColumns(columns)}
            columnFilter={columnFilter}
            onMoveColumn={moveColumn}
            hiddenColumnKeys={hiddenColumnKeys}
            shownColumnKeys={shownColumnKeys}
            onToggleColumn={toggleColumn}
            items={paginated.items}
            getRowId={site => site.id}
            requesting={requesting}
            emptyLabel="Keine statischen Seiten."
            onRowClick={openDetail}
            rowActions={rowActions}
            pagination={{
                count: paginated.total,
                page,
                limit,
                onPageChange: newPage => updateParams({ page: String(newPage) }),
                onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
            }}
        >
            <PaloConfirmationDialog
                isVisible={!!toDelete}
                onConfirm={handleDelete}
                onCancel={() => setToDelete(null)}
                title={<FormattedMessage id="app.contentManagement.staticSites.delete" />}
                action={<FormattedMessage id="app.common.delete" />}
            >
                <PoolFormattedMessage
                    id="app.contentManagement.staticSites.deleteWarning"
                    values={{ name: paginated.items.find(item => item.id === toDelete)?.title[currentLocale] }}
                />
            </PaloConfirmationDialog>
        </PaloListView>
    );
};
